import { strToU8, zipSync } from "fflate";

export type SpreadsheetCell = string | number | boolean | null | undefined;

export type SpreadsheetSheet = {
  readonly name: string;
  readonly rows: readonly (readonly SpreadsheetCell[])[];
};

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const SPREADSHEET_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const RELATIONSHIP_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

export const workbookXlsxBytes = (sheets: readonly SpreadsheetSheet[]): Uint8Array => {
  if (sheets.length === 0) throw new Error("A workbook needs at least one sheet");
  const normalizedSheets = normalizeSheets(sheets);
  const files: Record<string, Uint8Array> = {
    "[Content_Types].xml": xmlBytes(contentTypesXml(normalizedSheets.length)),
    "_rels/.rels": xmlBytes(rootRelationshipsXml()),
    "docProps/app.xml": xmlBytes(appPropertiesXml(normalizedSheets)),
    "docProps/core.xml": xmlBytes(corePropertiesXml()),
    "xl/workbook.xml": xmlBytes(workbookXml(normalizedSheets)),
    "xl/_rels/workbook.xml.rels": xmlBytes(workbookRelationshipsXml(normalizedSheets.length)),
    "xl/styles.xml": xmlBytes(stylesXml()),
  };
  normalizedSheets.forEach((sheet, index) => {
    files[`xl/worksheets/sheet${index + 1}.xml`] = xmlBytes(worksheetXml(sheet.rows));
  });
  return zipSync(files, { level: 6 });
};

const normalizeSheets = (sheets: readonly SpreadsheetSheet[]): SpreadsheetSheet[] => {
  const used = new Set<string>();
  return sheets.map((sheet, index) => {
    const base = (sheet.name.trim() || `Sheet ${index + 1}`)
      .replace(/[\\/?*:[\]]/g, " ")
      .replace(/\s+/g, " ")
      .slice(0, 31);
    let name = base;
    let suffix = 2;
    while (used.has(name.toLowerCase())) {
      const marker = ` ${suffix++}`;
      name = base.slice(0, 31 - marker.length) + marker;
    }
    used.add(name.toLowerCase());
    return { name, rows: sheet.rows };
  });
};

const worksheetXml = (rows: readonly (readonly SpreadsheetCell[])[]): string => {
  const columnCount = rows.reduce((maximum, row) => Math.max(maximum, row.length), 0);
  const columnWidths = Array.from({ length: columnCount }, (_, columnIndex) => {
    const width = rows.reduce((maximum, row) => {
      const value = row[columnIndex];
      return Math.max(maximum, displayCellValue(value).length);
    }, 0);
    return Math.max(10, Math.min(55, width + 2));
  });
  const columns = columnWidths.length === 0
    ? ""
    : `<cols>${columnWidths.map((width, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`,
      ).join("")}</cols>`;
  const sheetRows = rows.map((row, rowIndex) => {
    const rowNumber = rowIndex + 1;
    const cells = row.map((value, columnIndex) =>
      cellXml(value, columnIndex, rowNumber, rowIndex === 0),
    ).join("");
    return `<row r="${rowNumber}">${cells}</row>`;
  }).join("");
  const lastColumn = columnCount === 0 ? "A" : columnName(columnCount - 1);
  const lastRow = Math.max(1, rows.length);
  return XML_HEADER +
    `<worksheet xmlns="${SPREADSHEET_NS}" xmlns:r="${RELATIONSHIP_NS}">` +
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    `<dimension ref="A1:${lastColumn}${lastRow}"/>` +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    columns +
    `<sheetData>${sheetRows}</sheetData>` +
    (rows.length > 0 && columnCount > 0 ? `<autoFilter ref="A1:${lastColumn}${lastRow}"/>` : "") +
    "</worksheet>";
};

const cellXml = (
  value: SpreadsheetCell,
  columnIndex: number,
  rowNumber: number,
  header: boolean,
): string => {
  const reference = columnName(columnIndex) + rowNumber;
  const style = header ? 1 : 2;
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${reference}" s="${style}" t="n"><v>${value}</v></c>`;
  }
  if (typeof value === "boolean") {
    return `<c r="${reference}" s="${style}" t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  const text = displayCellValue(value);
  const preserve = /^\s|\s$/.test(text) ? ' xml:space="preserve"' : "";
  return `<c r="${reference}" s="${style}" t="inlineStr"><is><t${preserve}>${xmlEscape(text)}</t></is></c>`;
};

const displayCellValue = (value: SpreadsheetCell): string =>
  value === null || value === undefined ? "" : String(value);

const columnName = (index: number): string => {
  let current = index + 1;
  let name = "";
  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }
  return name;
};

const workbookXml = (sheets: readonly SpreadsheetSheet[]): string =>
  XML_HEADER +
  `<workbook xmlns="${SPREADSHEET_NS}" xmlns:r="${RELATIONSHIP_NS}"><sheets>` +
  sheets.map((sheet, index) =>
    `<sheet name="${xmlEscape(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`,
  ).join("") +
  '</sheets><calcPr calcId="0"/></workbook>';

const workbookRelationshipsXml = (sheetCount: number): string =>
  XML_HEADER +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  Array.from({ length: sheetCount }, (_, index) =>
    `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`,
  ).join("") +
  `<Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
  "</Relationships>";

const rootRelationshipsXml = (): string =>
  XML_HEADER +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>' +
  '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>' +
  "</Relationships>";

const contentTypesXml = (sheetCount: number): string =>
  XML_HEADER +
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
  '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>' +
  '<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>' +
  Array.from({ length: sheetCount }, (_, index) =>
    `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
  ).join("") +
  "</Types>";

const stylesXml = (): string =>
  XML_HEADER +
  `<styleSheet xmlns="${SPREADSHEET_NS}">` +
  '<fonts count="2"><font><sz val="11"/><name val="Aptos"/></font><font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Aptos"/></font></fonts>' +
  '<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF334155"/><bgColor indexed="64"/></patternFill></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"><alignment vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf></cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

const appPropertiesXml = (sheets: readonly SpreadsheetSheet[]): string =>
  XML_HEADER +
  '<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">' +
  '<Application>Lawstream</Application><HeadingPairs><vt:vector size="2" baseType="variant"><vt:variant><vt:lpstr>Worksheets</vt:lpstr></vt:variant><vt:variant><vt:i4>' +
  sheets.length +
  '</vt:i4></vt:variant></vt:vector></HeadingPairs><TitlesOfParts><vt:vector size="' +
  sheets.length +
  '" baseType="lpstr">' +
  sheets.map((sheet) => `<vt:lpstr>${xmlEscape(sheet.name)}</vt:lpstr>`).join("") +
  "</vt:vector></TitlesOfParts></Properties>";

const corePropertiesXml = (): string =>
  XML_HEADER +
  '<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:creator>Lawstream</dc:creator><cp:lastModifiedBy>Lawstream</cp:lastModifiedBy></cp:coreProperties>';

const xmlBytes = (value: string): Uint8Array => strToU8(value);

const xmlEscape = (value: string): string =>
  xmlSafeText(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const xmlSafeText = (value: string): string =>
  [...value].filter((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint !== undefined &&
      (codePoint === 9 || codePoint === 10 || codePoint === 13 || codePoint >= 32);
  }).join("");
